import Script from 'next/script';

import BaseContainer from '@commons/containers/BaseContainer';
import type { DrupalBlock } from '@commons/types/drupal';

import PlaceholderBlock from '../Placeholder';

// Type definition for the SurveyMonkeyBlock component properties.

type Props = DrupalBlock<{
  surveyMonkeyId: string;
}>;

/**
 * SurveyMonkeyBlock component.
 *
 * Renders a SurveyMonkey embedded form based on the provided ID.
 *
 * In the editor, it shows a placeholder with the SurveyMonkey ID.
 *
 * IMPORTANT:
 *
 * Using the embed code directly does not work in a Next.js environment.
 * This is because the JavaScript tries to find the last script tag in the document,
 * thinking it's the embed code itself. However, Next.js adds script tags sporadically.
 *
 * In order to work around this, the code has been modified to look for a specific script tag ID.
 */

const SurveyMonkeyBlock = ({ content }: Props) => {
  const { surveyMonkeyId } = content;

  return (
    <PlaceholderBlock placeholder={`Survey Monkey: ${surveyMonkeyId}`}>
      <BaseContainer id={surveyMonkeyId} width={12}>
        <Script id="survey-monkey-init" strategy="afterInteractive">
          {`(function (window, document, surveyMonkeyId) {
          var wrapperTag, scriptTag;
          (window.SMCX = window.SMCX || []),
            document.getElementById('smcx-sdk') ||
            ((wrapperTag = document.getElementById(surveyMonkeyId)),
            (scriptTag = document.createElement('script')),
            (scriptTag.type = "text/javascript"),
            (scriptTag.async = !0),
            (scriptTag.id = "smcx-sdk"),
            (scriptTag.src = "https://widget.surveymonkey.com/collect/website/js/" + surveyMonkeyId + ".js"),
            wrapperTag.appendChild(scriptTag));
        })(window, document, "${surveyMonkeyId}");`}
        </Script>
      </BaseContainer>
    </PlaceholderBlock>
  );
};

export default SurveyMonkeyBlock;
